import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { ShieldCheck, MailCheck } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import PasswordInput from "@/components/PasswordInput";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // New: after a successful sign-up the backend returns
  // `pending_approval: true` instead of an auth cookie. We render a
  // dedicated confirmation panel so the user understands why they are
  // NOT being routed into the data room immediately.
  const [submittedEmail, setSubmittedEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const result = await register(email, password, name);
      if (result?.pending_approval) {
        setSubmittedEmail(email);
        return; // stay on this page — show the pending panel
      }
      navigate("/app/rooms");
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <ThemeToggle />
      <div className="w-full max-w-md border border-border bg-card p-10 relative overflow-hidden">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-display text-base font-medium">Sovereign VDR</span>
        </div>
        {submittedEmail ? (
          <div data-testid="register-pending-panel" className="space-y-5">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <MailCheck className="w-6 h-6 text-emerald-500" />
            </div>
            <h1 className="font-display text-3xl tracking-tight font-medium">
              Request sent
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Thanks — your access request has been sent to the App Owners.
              You'll receive an email at{" "}
              <span className="mono text-foreground">{submittedEmail}</span>{" "}
              once a decision is made.
            </p>
            <p className="text-sm text-muted-foreground">
              You won't be able to sign in until an Owner approves your account.
            </p>
            <Link to="/" className="inline-block label-mono text-primary hover:underline">
              — Back to overview
            </Link>
          </div>
        ) : (<>
        <h1 className="font-display text-3xl tracking-tight font-medium">Request access</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Submit a sign-up request. An App Owner will review and assign your
          role before you can sign in.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="label-mono">Full name</label>
            <input
              data-testid={AUTH.registerName}
              type="text" required value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="label-mono">Work email</label>
            <input
              data-testid={AUTH.registerEmail}
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="label-mono">Password</label>
            <PasswordInput
              testId={AUTH.registerPassword}
              required minLength={10} value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1"
            />
            <div className="mono text-[10px] text-muted-foreground mt-1">
              Min 10 chars · upper + lower + digit + symbol.
            </div>
          </div>
          <div>
            <label className="label-mono">Confirm password</label>
            <PasswordInput
              testId="register-confirm-input"
              required minLength={10} value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1"
            />
            {confirm && password && confirm !== password && (
              <div className="text-xs text-destructive mt-1">Passwords do not match.</div>
            )}
          </div>
          {err && <div className="text-sm text-destructive">{err}</div>}
          <button
            data-testid={AUTH.registerSubmit}
            type="submit" disabled={loading}
            className="w-full px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
          >
            {loading ? "Creating…" : "Request access"}
          </button>
        </form>
        <div className="mt-6 text-sm text-muted-foreground">
          Already registered? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
        </div>
        </>)}
      </div>
    </div>
  );
}
